import React, { useEffect, useState } from 'react';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PricingStep = ({ formData, onChange, errors, venuePaymentConfig }) => {

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  const hasVenuePayment = Boolean(
    venuePaymentConfig?.bankName ||
    venuePaymentConfig?.accountNumber ||
    venuePaymentConfig?.accountHolder
  );

  useEffect(() => {
    if (!hasVenuePayment) return;
    const nextBankName = venuePaymentConfig?.bankName || '';
    const nextAccountNumber = venuePaymentConfig?.accountNumber || '';
    const nextAccountHolder = venuePaymentConfig?.accountHolder || '';

    if (
      (formData?.bankName || '') === nextBankName &&
      (formData?.accountNumber || '') === nextAccountNumber &&
      (formData?.accountHolder || '') === nextAccountHolder
    ) {
      return;
    }

    onChange({
      ...formData,
      bankName: nextBankName,
      accountNumber: nextAccountNumber,
      accountHolder: nextAccountHolder
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasVenuePayment, venuePaymentConfig]);

  useEffect(() => {
    if (paymentModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [paymentModalOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e?.key === 'Escape' && paymentModalOpen) {
        setPaymentModalOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [paymentModalOpen]);

  const handleChange = (field, value) => {
    onChange({ ...formData, [field]: value });
  };

  const hasPaymentInfo = Boolean(
    (formData?.bankName || '').trim() &&
    (formData?.accountNumber || '').trim() &&
    (formData?.accountHolder || '').trim() &&
    (formData?.paymentDeadline || '').trim()
  );

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="bg-card border border-border rounded-xl p-4 md:p-6 mt-4">
        <Input
          label="가격"
          type="number"
          placeholder="예: 10000"
          value={formData?.memberPrice || ''}
          onChange={(e) => handleChange('memberPrice', e?.target?.value)}
          error={errors?.memberPrice}
          required
          min="0"
          description="참가자 1인당 참가비 (원)"
          className="bg-muted/30"
        />

        <div className="space-y-4 mt-6">
          <h4 className="font-heading font-medium text-foreground">결제 정보</h4>

          <div className="bg-muted/40 rounded-md border border-border p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs caption text-muted-foreground">현재 결제 정보</p>
                <p className="text-sm font-medium text-foreground mt-1 truncate">
                  {(formData?.bankName || '').trim() ? `${formData?.bankName} ${formData?.accountNumber}` : '미등록'}
                </p>
                <p className="text-xs caption text-muted-foreground mt-1">
                  예금주: {(formData?.accountHolder || '').trim() || '미등록'} / 마감일: {formData?.paymentDeadline || '미등록'}
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  variant={hasPaymentInfo ? 'outline' : 'default'}
                  size="sm"
                  iconName={hasPaymentInfo ? 'Edit' : 'Plus'}
                  iconPosition="left"
                  onClick={() => setPaymentModalOpen(true)}
                >
                  {hasPaymentInfo ? '결제 정보 수정' : '결제 정보 등록'}
                </Button>
              </div>
            </div>

            {(errors?.bankName || errors?.accountNumber || errors?.accountHolder || errors?.paymentDeadline) && (
              <p className="text-sm text-destructive mt-3">
                결제 정보를 입력해야 다음 단계로 진행할 수 있습니다.
              </p>
            )}
          </div>
        </div>
      </div>

      {paymentModalOpen && (
        <div
          className="fixed inset-0 z-300 flex items-start justify-center pt-[8vh] px-4 overflow-y-auto"
          onClick={(e) => {
            if (e?.target === e?.currentTarget) setPaymentModalOpen(false);
          }}
        >
          <div className="fixed inset-0 bg-background/70 backdrop-blur-sm z-10" aria-hidden="true" />
          <div className="relative z-20 w-full max-w-3xl bg-card border border-border rounded-xl shadow-2xl overflow-hidden my-8">
            <div className="bg-card border-b border-border p-4 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-heading font-semibold text-foreground">결제 정보 {hasPaymentInfo ? '수정' : '등록'}</h3>
                <p className="text-xs caption text-muted-foreground mt-0.5">참가자에게 안내될 계좌/마감일을 입력하세요</p>
              </div>
              <button
                className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-athletic active-press"
                onClick={() => setPaymentModalOpen(false)}
                aria-label="닫기"
                type="button"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="p-4 md:p-6 space-y-4">
              <div className="bg-muted/40 rounded-md border border-border p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="은행명"
                    type="text"
                    placeholder="예: KB국민은행"
                    value={formData?.bankName || ''}
                    onChange={(e) => handleChange('bankName', e?.target?.value)}
                    error={errors?.bankName}
                    required
                    className="bg-muted/30"
                    disabled={hasVenuePayment}
                  />

                  <Input
                    label="계좌번호"
                    type="text"
                    placeholder="예: 123-456-789012"
                    value={formData?.accountNumber || ''}
                    onChange={(e) => handleChange('accountNumber', e?.target?.value)}
                    error={errors?.accountNumber}
                    required
                    className="bg-muted/30"
                    disabled={hasVenuePayment}
                  />

                  <Input
                    label="예금주"
                    type="text"
                    placeholder="예: 홍길동"
                    value={formData?.accountHolder || ''}
                    onChange={(e) => handleChange('accountHolder', e?.target?.value)}
                    error={errors?.accountHolder}
                    required
                    className="bg-muted/30"
                    disabled={hasVenuePayment}
                  />
                </div>

                <div className="flex justify-end pt-2">
                  <Button
                    variant="default"
                    iconName="Check"
                    iconPosition="right"
                    onClick={() => setPaymentModalOpen(false)}
                  >
                    완료
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-muted rounded-md p-4 md:p-6">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="font-heading font-medium text-foreground mb-2">
              가격 정보 요약
            </h4>
            <div className="space-y-2 text-sm caption text-muted-foreground">
              <div className="flex items-center justify-between">
                <span>가격</span>
                <span className="font-medium text-foreground data-text">
                  ₩{(parseInt(formData?.memberPrice) || 0)?.toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <span>결제 계좌</span>
                <span className="font-medium text-foreground data-text">
                  {formData?.bankName || '미입력'} {formData?.accountNumber || ''}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingStep;